import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Building2, User, Calendar, CheckCircle } from 'lucide-react';
import { apiRequest } from '@/lib/api';

interface AccessRequest {
  id: string;
  type: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  company_name: string;
  message: string;
  status: string;
}

interface ApproveRequestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  request: AccessRequest;
  onApprove: (data: any) => void;
  isSubmitting: boolean;
}

export function ApproveRequestDialog({
  isOpen,
  onClose,
  request,
  onApprove,
  isSubmitting
}: ApproveRequestDialogProps) {
  const [step, setStep] = useState(1);
  const [actionType, setActionType] = useState<'create_hub' | 'add_to_hub'>('create_hub');
  
  // Form data for creating new hub
  const [hubData, setHubData] = useState({
    hubName: '',
    companyName: request.company_name || '',
    country: 'United Kingdom',
    dataCentre: 'EMEA',
    subscriptionType: 'Free Trial',
    subscriptionStatus: 'Pending',
    startDate: new Date().toISOString().split('T')[0],
    endDate: ''
  });

  // Form data for super admin (when creating hub)
  const [superAdminData, setSuperAdminData] = useState({
    email: request.email,
    firstName: request.first_name || '',
    lastName: request.last_name || '',
    phone: request.phone || '',
    jobTitle: ''
  });

  // Form data for adding to existing hub
  const [existingHubId, setExistingHubId] = useState<string>('');
  const [availableHubs, setAvailableHubs] = useState<any[]>([]);
  const [userData, setUserData] = useState({
    email: request.email,
    firstName: request.first_name || '',
    lastName: request.last_name || '',
    permissionLevel: 'hub_super_user',
    projectId: '',
    phone: request.phone || '',
    jobTitle: ''
  });

  const [notes, setNotes] = useState('');

  // Fetch available hubs when dialog opens and actionType is 'add_to_hub'
  useEffect(() => {
    if (isOpen && actionType === 'add_to_hub' && step >= 2) {
      fetchHubs();
    }
  }, [isOpen, actionType, step]);

  const fetchHubs = async () => {
    try {
      // Try the admin-api-simple endpoint first
      const data = await apiRequest('/api/admin/hubs');
      // The response might be { success: true, hubs: [...] } or just [...]
      setAvailableHubs(data.hubs || data || []);
    } catch (error) {
      console.error('Failed to fetch hubs:', error);
      setAvailableHubs([]);
    }
  };

  // Auto-calculate data centre based on country
  useEffect(() => {
    const countryToDataCentre: Record<string, string> = {
      'United Kingdom': 'EMEA',
      'United States': 'US',
      'Canada': 'US',
      'Australia': 'APAC',
      'New Zealand': 'APAC',
    };
    setHubData(prev => ({
      ...prev,
      dataCentre: countryToDataCentre[prev.country] || 'EMEA'
    }));
  }, [hubData.country]);

  const handleNext = () => {
    if (step === 1) {
      // Validate action type selection
      if (!actionType) return;
    } else if (step === 2 && actionType === 'create_hub') {
      // Validate hub information
      if (!hubData.hubName || !hubData.companyName || !hubData.country) return;
    } else if (step === 2 && actionType === 'add_to_hub') {
      // Validate hub selection
      if (!existingHubId) return;
    } else if (step === 3 && actionType === 'create_hub') {
      // Validate super admin data
      if (!superAdminData.email || !superAdminData.firstName || !superAdminData.lastName) return;
    } else if (step === 3 && actionType === 'add_to_hub') {
      // Validate user data
      if (!userData.email || !userData.firstName || !userData.lastName || !userData.permissionLevel) return;
      if (userData.permissionLevel === 'project_admin' && !userData.projectId) return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = () => {
    const approveData: any = {
      actionType,
      notes
    };

    if (actionType === 'create_hub') {
      approveData.hubData = hubData;
      approveData.superAdminData = superAdminData;
    } else {
      approveData.existingHubId = existingHubId;
      approveData.userData = userData;
    }

    onApprove(approveData);
  };

  const resetForm = () => {
    setStep(1);
    setActionType('create_hub');
    setHubData({
      hubName: '',
      companyName: request.company_name || '',
      country: 'United Kingdom',
      dataCentre: 'EMEA',
      subscriptionType: 'Free Trial',
      subscriptionStatus: 'Pending',
      startDate: new Date().toISOString().split('T')[0],
      endDate: ''
    });
    setSuperAdminData({
      email: request.email,
      firstName: request.first_name || '',
      lastName: request.last_name || '',
      phone: request.phone || '',
      jobTitle: ''
    });
    setExistingHubId('');
    setUserData({
      email: request.email,
      firstName: request.first_name || '',
      lastName: request.last_name || '',
      permissionLevel: 'hub_super_user',
      projectId: '',
      phone: request.phone || '',
      jobTitle: ''
    });
    setNotes('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Approve Access Request</DialogTitle>
          <DialogDescription>
            {step === 1 && "Choose how to grant access to this user"}
            {step === 2 && actionType === 'create_hub' && "Enter hub information"}
            {step === 2 && actionType === 'add_to_hub' && "Select existing hub"}
            {step === 3 && actionType === 'create_hub' && "Set up super admin"}
            {step === 3 && actionType === 'add_to_hub' && "Set up user role"}
            {step === 4 && "Review and confirm"}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Select Action Type */}
        {step === 1 && (
          <div className="space-y-4">
            <RadioGroup value={actionType} onValueChange={(value: any) => setActionType(value)}>
              <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="create_hub" id="create_hub" />
                <Label htmlFor="create_hub" className="flex-1 cursor-pointer">
                  <div className="font-semibold">Create New Hub</div>
                  <div className="text-sm text-gray-500">Create a new hub for this company</div>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="add_to_hub" id="add_to_hub" />
                <Label htmlFor="add_to_hub" className="flex-1 cursor-pointer">
                  <div className="font-semibold">Add to Existing Hub</div>
                  <div className="text-sm text-gray-500">Add user to an existing hub</div>
                </Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Step 2A: Hub Information (Create Hub) */}
        {step === 2 && actionType === 'create_hub' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="hubName">Hub Name *</Label>
              <Input
                id="hubName"
                value={hubData.hubName}
                onChange={(e) => setHubData(prev => ({ ...prev, hubName: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name *</Label>
              <Input
                id="companyName"
                value={hubData.companyName}
                onChange={(e) => setHubData(prev => ({ ...prev, companyName: e.target.value }))}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Select value={hubData.country} onValueChange={(value) => setHubData(prev => ({ ...prev, country: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="Australia">Australia</SelectItem>
                    <SelectItem value="New Zealand">New Zealand</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="dataCentre">Data Centre</Label>
                <Input
                  id="dataCentre"
                  value={hubData.dataCentre}
                  readOnly
                  className="bg-gray-50"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="subscriptionType">Subscription Type *</Label>
                <Select value={hubData.subscriptionType} onValueChange={(value) => setHubData(prev => ({ ...prev, subscriptionType: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Free Trial">Free Trial</SelectItem>
                    <SelectItem value="Basic">Basic</SelectItem>
                    <SelectItem value="Enterprise Basic">Enterprise Basic</SelectItem>
                    <SelectItem value="Enterprise Premium">Enterprise Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subscriptionStatus">Subscription Status *</Label>
                <Select value={hubData.subscriptionStatus} onValueChange={(value) => setHubData(prev => ({ ...prev, subscriptionStatus: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={hubData.startDate}
                  onChange={(e) => setHubData(prev => ({ ...prev, startDate: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={hubData.endDate}
                  onChange={(e) => setHubData(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2B: Select Hub (Add to Hub) */}
        {step === 2 && actionType === 'add_to_hub' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="existingHub">Select Hub *</Label>
              <Select value={existingHubId} onValueChange={setExistingHubId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a hub" />
                </SelectTrigger>
                <SelectContent>
                  {availableHubs.map((hub) => (
                    <SelectItem key={hub.id} value={hub.id}>
                      {hub.hubName} - {hub.companyName} ({hub.country})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Step 3A: Super Admin Setup (Create Hub) */}
        {step === 3 && actionType === 'create_hub' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="superAdminEmail">Super Admin Email *</Label>
              <Input
                id="superAdminEmail"
                type="email"
                value={superAdminData.email}
                onChange={(e) => setSuperAdminData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="superAdminFirstName">First Name *</Label>
                <Input
                  id="superAdminFirstName"
                  value={superAdminData.firstName}
                  onChange={(e) => setSuperAdminData(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="superAdminLastName">Last Name *</Label>
                <Input
                  id="superAdminLastName"
                  value={superAdminData.lastName}
                  onChange={(e) => setSuperAdminData(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="superAdminPhone">Phone</Label>
                <Input
                  id="superAdminPhone"
                  type="tel"
                  value={superAdminData.phone}
                  onChange={(e) => setSuperAdminData(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="superAdminJobTitle">Job Title</Label>
                <Input
                  id="superAdminJobTitle"
                  value={superAdminData.jobTitle}
                  onChange={(e) => setSuperAdminData(prev => ({ ...prev, jobTitle: e.target.value }))}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3B: User Role Setup (Add to Hub) */}
        {step === 3 && actionType === 'add_to_hub' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="userEmail">Email *</Label>
              <Input
                id="userEmail"
                type="email"
                value={userData.email}
                onChange={(e) => setUserData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="userFirstName">First Name *</Label>
                <Input
                  id="userFirstName"
                  value={userData.firstName}
                  onChange={(e) => setUserData(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="userLastName">Last Name *</Label>
                <Input
                  id="userLastName"
                  value={userData.lastName}
                  onChange={(e) => setUserData(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="permissionLevel">Permission Level *</Label>
              <Select value={userData.permissionLevel} onValueChange={(value) => setUserData(prev => ({ ...prev, permissionLevel: value, projectId: '' }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hub_super_user">Hub Super User</SelectItem>
                  <SelectItem value="hub_admin">Hub Admin</SelectItem>
                  <SelectItem value="project_admin">Project Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {userData.permissionLevel === 'project_admin' && (
              <div className="space-y-2">
                <Label htmlFor="projectId">Project *</Label>
                <Select value={userData.projectId} onValueChange={(value) => setUserData(prev => ({ ...prev, projectId: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* TODO: Fetch projects for selected hub */}
                    <SelectItem value="project1">Project 1</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="userPhone">Phone</Label>
                <Input
                  id="userPhone"
                  type="tel"
                  value={userData.phone}
                  onChange={(e) => setUserData(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="userJobTitle">Job Title</Label>
                <Input
                  id="userJobTitle"
                  value={userData.jobTitle}
                  onChange={(e) => setUserData(prev => ({ ...prev, jobTitle: e.target.value }))}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Review & Confirm */}
        {step === 4 && (
          <div className="space-y-4">
            {actionType === 'create_hub' ? (
              <>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Hub Information
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                    <div><strong>Hub Name:</strong> {hubData.hubName}</div>
                    <div><strong>Company:</strong> {hubData.companyName}</div>
                    <div><strong>Country:</strong> {hubData.country}</div>
                    <div><strong>Data Centre:</strong> {hubData.dataCentre}</div>
                    <div><strong>Subscription:</strong> {hubData.subscriptionType} ({hubData.subscriptionStatus})</div>
                    <div><strong>Start Date:</strong> {hubData.startDate}</div>
                    {hubData.endDate && <div><strong>End Date:</strong> {hubData.endDate}</div>}
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Super Admin
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                    <div><strong>Email:</strong> {superAdminData.email}</div>
                    <div><strong>Name:</strong> {superAdminData.firstName} {superAdminData.lastName}</div>
                    {superAdminData.phone && <div><strong>Phone:</strong> {superAdminData.phone}</div>}
                    {superAdminData.jobTitle && <div><strong>Job Title:</strong> {superAdminData.jobTitle}</div>}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Selected Hub
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg text-sm">
                    {availableHubs.find(h => h.id === existingHubId)?.hubName || 'N/A'}
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <User className="w-4 h-4" />
                    User Information
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                    <div><strong>Email:</strong> {userData.email}</div>
                    <div><strong>Name:</strong> {userData.firstName} {userData.lastName}</div>
                    <div><strong>Permission Level:</strong> {userData.permissionLevel}</div>
                    {userData.projectId && <div><strong>Project:</strong> {userData.projectId}</div>}
                    {userData.phone && <div><strong>Phone:</strong> {userData.phone}</div>}
                    {userData.jobTitle && <div><strong>Job Title:</strong> {userData.jobTitle}</div>}
                  </div>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <textarea
                id="notes"
                className="w-full min-h-[100px] p-2 border rounded-md"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes..."
              />
            </div>
            <div className="flex items-center space-x-2 p-4 bg-blue-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-blue-800">
                I confirm that all information is correct and ready to approve this request.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {step > 1 && (
            <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
              Back
            </Button>
          )}
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {step < 4 ? (
            <Button onClick={handleNext} disabled={isSubmitting}>
              Next
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? 'Approving...' : actionType === 'create_hub' ? 'Approve & Create' : 'Approve & Add User'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

